import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { membershipApi, billingApi } from '../services/api';
import { useAuth } from '../context/AuthContext';

const MembershipSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { checkAuth } = useAuth();
  const [status, setStatus] = useState('loading'); // loading, success, failed
  const [paymentData, setPaymentData] = useState(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      navigate('/membership');
      return;
    }

    const pollStatus = async (attempts = 0) => {
      const maxAttempts = 10;
      const pollInterval = 2000;

      if (attempts >= maxAttempts) {
        setStatus('failed');
        return;
      }

      try {
        // Try new billing endpoint first, fall back to legacy
        let data;
        try {
          const resp = await billingApi.checkoutStatus(sessionId);
          data = resp.data;
        } catch {
          const resp = await membershipApi.checkStatus(sessionId);
          data = resp.data;
        }
        setPaymentData(data);

        if (data.payment_status === 'paid' || data.status === 'complete') {
          setStatus('success');
          await checkAuth();
          return;
        } else if (data.status === 'expired') {
          setStatus('failed');
          return;
        }

        setTimeout(() => pollStatus(attempts + 1), pollInterval);
      } catch (error) {
        console.error('Error checking status:', error);
        setTimeout(() => pollStatus(attempts + 1), pollInterval);
      }
    };

    pollStatus();
  }, [sessionId, navigate, checkAuth]);

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Header />

      <div className="flex-1 flex items-center justify-center py-16">
        <div className="max-w-md w-full mx-auto px-4">
          {status === 'loading' && (
            <div className="text-center">
              <Loader2 className="w-16 h-16 text-[#2563EB] mx-auto mb-6 animate-spin" />
              <h1 className="text-2xl font-bold text-slate-900 mb-2">Processing Payment</h1>
              <p className="text-slate-600">Please wait while we confirm your payment...</p>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2">Payment Successful!</h1>
              <p className="text-slate-600 mb-8">
                Your membership has been upgraded. Thank you for choosing EaglesMarket!
              </p>
              {paymentData && paymentData.amount_total && (
                <div className="bg-white border border-slate-200 rounded-xl p-6 mb-8 text-left">
                  <h3 className="font-semibold text-slate-900 mb-4">Payment Details</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Amount</span>
                      <span className="text-slate-900 font-medium">
                        ${(paymentData.amount_total / 100).toFixed(2)} {paymentData.currency?.toUpperCase()}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Status</span>
                      <span className="text-green-600 font-medium">Paid</span>
                    </div>
                  </div>
                </div>
              )}
              <div className="flex gap-4">
                <Button 
                  onClick={() => navigate('/dashboard')}
                  className="flex-1 bg-slate-900 hover:bg-slate-800"
                  data-testid="success-dashboard-btn"
                >
                  Go to Dashboard
                </Button>
                <Button 
                  onClick={() => navigate('/')}
                  variant="outline"
                  className="flex-1"
                >
                  Back to Home
                </Button>
              </div>
            </div>
          )}

          {status === 'failed' && (
            <div className="text-center">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-12 h-12 text-red-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2">Payment Failed</h1>
              <p className="text-slate-600 mb-8">
                We couldn't process your payment. Please try again or contact support.
              </p>
              <div className="flex gap-4">
                <Button 
                  onClick={() => navigate('/membership')}
                  className="flex-1 bg-slate-900 hover:bg-slate-800"
                  data-testid="failed-retry-btn"
                >
                  Try Again
                </Button>
                <Button 
                  onClick={() => navigate('/')}
                  variant="outline"
                  className="flex-1"
                >
                  Back to Home
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default MembershipSuccessPage;
